import { storageDocuments } from "@/firebase/firebaseConfig";
import { createHash } from "crypto";
import moment from "moment";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import isNull from "lodash/isNull";
import isString from "lodash/isString";
import isObject from "lodash/isObject";
import isNil from "lodash/isNil";
import { saveAs } from "file-saver";
import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();

export const operationLoadingStart = (
  vc //  VueComponent
) => {
  vc.$vs.loading({ color: AISEA_COLOR.BLUE });
};

export const operationLoadingClose = (
  vc //  VueComponent
) => {
  vc.$vs.loading.close();
};

// システムエラー通知
export const notifySystemError = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "bottom-right",
  }
) => {
  const i18n = vc.$t("Views.Operation.Error.SystemError");
  vc.$vs.notify({
    title: i18n.title,
    text: i18n.text,
    color: "danger",
    iconPack: "feather",
    icon: "icon-x-square",
    ...options,
  });
};

// 404エラー通知
export const notifyNotFoundError = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "bottom-right",
  }
) => {
  const i18n = vc.$t("Views.Operation.Error.NotFoundError");
  vc.$vs.notify({
    title: i18n.title,
    color: "danger",
    ...options,
  });
};

// レポート削除成功通知
export const notifyDeleteSuccess = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "top-center",
  }
) => {
  const i18n = vc.$t("Views.Operation.Delete.Success");
  vc.$vs.notify({
    text: i18n.text,
    color: "success",
    ...options,
  });
};

// レポート削除失敗通知
export const notifyDeleteFailed = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "top-center",
  }
) => {
  const i18n = vc.$t("Views.Operation.Delete.Fail");
  vc.$vs.notify({
    text: i18n.text,
    color: "danger",
    ...options,
  });
};

// レポート画像ダウンロード失敗通知
export const notifyDownloadImageFailed = (
  vc, //  VueComponent
  options = {}
) => {
  const i18n = vc.$t("Views.Operation.ImageManipulation.Download.Fail");
  vc.$vs.notify({
    text: i18n.text,
    color: "danger",
    fixed: false,
    position: "bottom-right",
    ...options,
  });
};

// トランザクション成功通知(デフォルト: 差し戻し)
export const notifyTransactionSuccess = (
  vc, //  VueComponent
  transactionLabel, // string
  options = {}
) => {
  const isLabelValid =
    isString(transactionLabel) && transactionLabel.length > 0;
  if (!isLabelValid) {
    throw new Error("Invalid transaction label");
  }
  const text = vc.$t(`Views.Operation.Transaction.Success.text`, {
    transactionLabel,
  });
  vc.$vs.notify({
    text,
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// トランザクション失敗通知
export const notifyTransactionFailed = (
  vc, //  VueComponent
  transactionLabel, // string
  options = {}
) => {
  const isLabelValid =
    isString(transactionLabel) && transactionLabel.length > 0;
  if (!isLabelValid) {
    throw new Error("Invalid transaction label");
  }
  const text = vc.$t(`Views.Operation.Transaction.Fail.text`, {
    transactionLabel,
  });
  vc.$vs.notify({
    text,
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};
// データ更新成功通知
export const notifyUpdateSuccess = (
  vc, //  VueComponent
  options = {}
) => {
  const text = vc.$t(`Views.Operation.Edit.Success.text`);
  vc.$vs.notify({
    text,
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// データ更新失敗通知
export const notifyUpdateFailed = (
  vc, //  VueComponent
  options = {}
) => {
  const text = vc.$t(`Views.Operation.Edit.Fail.text`);
  vc.$vs.notify({
    text,
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// 一時ファイル作成成功通知
export const notifyUploadTempImageSuccess = (
  vc, //  VueComponent
  options = {}
) => {
  vc.$vs.notify({
    text: "画像ファイルの追加に成功しました",
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// 一時ファイル作成失敗通知
export const notifyUploadTempImageFailed = (
  vc, //  VueComponent
  options = {}
) => {
  // TODO: i18
  vc.$vs.notify({
    text: "画像ファイルの追加に失敗しました",
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// レポート一覧画面に遷移
export const pushReportList = (
  vc //  VueComponent
) => {
  vc.$router.push({
    name: "addon-operation-reports-index",
  });
};

// 詳細・編集に表示する更新日時フォーマットに変換
export const formatDateTime = (
  dateString // like 2024-03-15T09:06:08.000Z
) => {
  return moment(dateString).format("YYYY/MM/DD HH:mm"); // string
};

// レポートの値がstringでformatがある場合にフォーマットを適用する
// NOTE: 現在は日時に関わるデータの場合しか想定していない
export const applyFormat = (reportValue, enterpriseForm) => {
  if (!enterpriseFormService.hasFormat(enterpriseForm)) {
    return reportValue;
  }
  if (!isString(reportValue)) return reportValue; // any

  const isTime = enterpriseFormService.isTimePickers(enterpriseForm);
  const dateValue = isTime
    ? moment(reportValue, "HH:mm:ss") // Time
    : moment(reportValue); // Date / DateTime
  return dateValue.format(enterpriseForm.format); // string
};

// レポートの値に単位を適用する
export const applyUnit = (
  reportValue, // string | number | null;
  enterpriseForm // EnterpriseForm;
) => {
  if (isNull(reportValue)) return reportValue;
  if (!enterpriseFormService.hasUnit(enterpriseForm)) {
    return reportValue;
  }
  return `${reportValue} ${enterpriseForm.unit}`.trim();
};

// storageのパスを生成
export const createStorageFilePath = (
  enterpriseId, // string
  reportId, // string
  formVersionItemId, // string => マスタの方
  fileName // string
) => {
  return `e:${enterpriseId}/operation/reports:${reportId}/items:${formVersionItemId}/${fileName}`; // string
};

// 画像を一意に特定するためにhashを作成するのに使用
export const createOperationHash = (
  message = "" // : string
) => {
  const hash = createHash("sha256");
  hash.update(message);
  return hash.digest("hex"); // string
};

const isDownloadImageValid = (
  image // {url: string; name: string;}
) => {
  if (!isObject(image)) return false;
  const { url, name } = image;
  return isString(url) && url !== "" && isString(name) && name !== "";
};

/**
 * Downloads an image from the specified URL and saves it with the given name.
 * @param {Object} options - The options for downloading the image.
 * @param {string} options.url - The URL of the image to download.
 * @param {string} options.name - The name to save the downloaded image as.
 * @returns {Promise<void>} - A promise that resolves when the image is downloaded and saved.
 * @throws {Error} If the image data is invalid.
 */
export const downloadImage = async ({
  url, // : string
  name, // string
}) => {
  if (!isDownloadImageValid({ url, name })) {
    throw new Error("Invalid image data.");
  }
  saveAs(url, name);
};

// 画像のURLからblobを作成する
export const getBlogUrl = async (
  url // : string
) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

// 画像のblobからdataURLを作成する -> 何度も画像のURLにアクセスしないようにするため
export const readFileAsDataURL = async blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject();
    };
    reader.readAsDataURL(blob);
  });
};

// 画像のURLからblobを作成してdataURLに変換する
export const getDataURLFromStoragePath = async (
  path // string
) => {
  const imageUrl = await storageDocuments.child(path).getDownloadURL();
  const blob = await getBlogUrl(imageUrl);
  const dataURL = await readFileAsDataURL(blob);
  return dataURL;
};

// routeが詳細画面か判定
export const isShowScreen = (
  route // Vue-Router's Route object
) => {
  if (!isObject(route)) return false;
  return route.name === "addon-operation-reports-show";
};

// routeが編集画面か判定
export const isEditScreen = (
  route // Vue-Router's Route object
) => {
  if (!isObject(route)) return false;
  return route.name === "addon-operation-reports-edit";
};

// 編集画面に遷移
export const pushEditScreen = (
  vc, // VueComponent { $router: VueRouter; }
  { aoEnterpriseId, reportId } // router parameter object { aoEnterpriseId: `${number}`; reportId: `${number}`; }
) => {
  if (!vc.$store.getters["operationReport/isReportUpdatable"]) return false; // 編集権限がない場合は遷移しない
  if (isNaN(aoEnterpriseId) || isNaN(reportId)) return false; // どちらかが数値でない場合は遷移しない
  vc.$router.push({
    name: "addon-operation-reports-edit",
    aoEnterpriseId,
    reportId,
  });
};

// 詳細画面に遷移(同一タブ)
export const pushShowScreen = (
  vc, // VueComponent { $router: VueRouter; }
  { aoEnterpriseId, reportId } // router parameter object { aoEnterpriseId: `${number}`; reportId: `${number}`; }
) => {
  if (isNaN(aoEnterpriseId) || isNaN(reportId)) return false; // どちらかが数値でない場合は遷移しない
  vc.$router.push({
    name: "addon-operation-reports-show",
    aoEnterpriseId,
    reportId,
  });
};

// NOTE: レポート自体の編集権限があるかどうかの判定
// 個別のフォームの編集権限は別途判定する必要がある
export const isEditingReport = (
  vc // VueComponent
) => {
  if (!vc.$store.getters["operationReport/isReportUpdatable"]) return false; // 編集権限がない場合はfalse
  if (!isObject(vc.$route.params)) return false;
  const { aoEnterpriseId, reportId } = vc.$route.params;
  return (
    vc.$route.name === "addon-operation-reports-edit" &&
    !isNaN(aoEnterpriseId) &&
    !isNaN(reportId)
  );
};

// formの情報とレポートの情報からデフォルト値を取得する
// default_value があればそれを返す
// default_value_id があればそこから計算した値を返す
// それ以外はnullを返す
export const calcDefaultValue = (
  vc, // VueComponent
  enterpriseFormVersionItem
) => {
  if (enterpriseFormService.hasDefaultValue(enterpriseFormVersionItem)) {
    const defaultValue = enterpriseFormService.getDefaultValue(
      enterpriseFormVersionItem
    );
    if (defaultValue === "NOW") return moment().toISOString();
    return defaultValue; // default_value
  }
  const reportFormVersionItemMap =
    vc.$store.getters[
      "operationReport/reportFormVersionItemMapByFormVersionItemId"
    ];
  return enterpriseFormService.getDefaultValueByDefaultValueId(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ); // default_value_id から導いた値 | null
};

// 更新時に使用するformの値を作成する
export const calcNextFormItemValue = (
  // vc // VueComponent
  formVersionItem, // {input_type: number; }
  reportFormVersionItem, // {form_version_item_id: `${number}`}
  nextFormValue // { value: number | string | date | [{ "name": string; }], nullableChecked: boolean }
) => {
  // {
  // formVersionSortOrder: number;
  //        {
  //           "form_version_item_id": `${number}`,
  //           "nullable_checked": boolean | null,
  //           "value_date": string | null; "2024-04-18"
  //           "value_date_time": string | null; "2024-04-18T05:00:21.564Z"
  //           "value_files": [
  //             {
  //               "name": string | null; // NOTE: 一時ファイルを作成しておく必要がある
  //             }
  //           ],
  //           "value_number": number;
  //           "value_string": string | null;
  //           "value_time": string | null; "05:00:21"
  //         }
  // }
  const nullableChecked = nextFormValue.nullableChecked === true;
  const nextValue = calcNextValueWithInputType(formVersionItem, nextFormValue);
  return {
    ...reportFormVersionItem,
    ...nextValue,
    nullable_checked: nullableChecked,
    form_version_item_id: formVersionItem.id,
  };
};

const calcNextValueWithInputType = (
  formVersionItem, // {input_type: number; }
  nextFormValue // { value: number | string | date | [{ "name": string; }] }
) => {
  const key = enterpriseFormService.getValueType(formVersionItem);
  const nextValue = calcNextValue(formVersionItem, nextFormValue);

  return { [key]: nextValue };
  // return { [key]: nextFormValue.value };
  // {"value_date": string | null; "2024-04-18"} | {"value_date_time": string | null; "2024-04-18T05:00:21.564Z"}
  // | {
  //     "value_files": [
  //             {
  //               "name": string | null; // NOTE: 一時ファイルを作成しておく必要がある
  //             }
  //           ],
  //   }
  // | {"value_number": number;}
  // | {"value_string": string | null;}
  // | {"value_time": string | null; "05:00:21"}
};

const calcNextValue = (
  formVersionItem, // {input_type: number; }
  nextFormValue // { value: number | string | date | [{ "name": string; }] }
) => {
  // NOTE:
  // 0: FIXED (固定値): 未実装
  // 1: DATE_PICKER (年月日)
  // 2: TIME_PICKER (時間)
  // 3: SPACE（レイアウト調整用）: 未実装
  // 4: DATETIME_PICKER (日時)
  // 5: SELECT (選択項目)
  // 6: HIERARCHICAL_SELECT (２階層選択): 未実装
  // 7: CHECK_BOX (チェックボックス): 未実装
  // 8: RADIO_BUTTON (ラジオボタン)
  // 9: NUMBER (数値入力)
  // 10: SHORT_TEXT  (短いテキスト入力)
  // 11: LONG_TEXT  (長いテキスト入力)
  // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード) 個別に対応
  const efs = enterpriseFormService;
  if (efs.isDatePickers(formVersionItem))
    return calcValueDate(nextFormValue.value);
  if (efs.isNumber(formVersionItem)) {
    if (isNil(nextFormValue.value)) return null;
    if (nextFormValue.value === "") return null;
    if (isNaN(nextFormValue.value)) return null;
    return Number(nextFormValue.value);
  }
  return nextFormValue.value;
};

// Dateオブジェクトを value_date に変換
export const calcValueDate = (
  date // Date
) => {
  const momentDate = moment(date);
  if (!momentDate.isValid()) return "";
  const valueDate = momentDate.format("YYYY-MM-DD");
  return valueDate; // string like "2024-04-18"
};

// 編集フォームで使用するコンポーネント名を取得
export const calcEditComponentName = (
  formVersionItem // {input_type: number; }
) => {
  // NOTE:
  // 0: FIXED (固定値): 未実装
  // 1: DATE_PICKER (年月日)
  // 2: TIME_PICKER (時間)
  // 3: SPACE（レイアウト調整用）: 未実装
  // 4: DATETIME_PICKER (日時)
  // 5: SELECT (選択項目)
  // 6: HIERARCHICAL_SELECT (２階層選択): 未実装
  // 7: CHECK_BOX (チェックボックス): 未実装
  // 8: RADIO_BUTTON (ラジオボタン)
  // 9: NUMBER (数値入力)
  // 10: SHORT_TEXT  (短いテキスト入力)
  // 11: LONG_TEXT  (長いテキスト入力)
  // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード) 個別に対応
  const efs = enterpriseFormService;
  if (efs.isDatePickers(formVersionItem)) return "form-item-date-picker";
  if (efs.isTimePickers(formVersionItem)) return "form-item-time-picker";
  if (efs.isDateTimePickers(formVersionItem)) {
    return "form-item-date-time-picker";
  }
  if (efs.isSpace(formVersionItem)) {
    return "form-item-space";
  }
  if (efs.isSelect(formVersionItem)) return "form-item-select";
  if (efs.isRadioButton(formVersionItem)) return "form-item-radio-buttons";
  if (efs.isNumber(formVersionItem)) return "form-item-number";
  if (efs.isShortText(formVersionItem)) return "form-item-short-text";
  if (efs.isLongText(formVersionItem)) return "form-item-long-text";
  // if (efs.isSingleImageFile(formVersionItem)) return "form-item-long-text";
  throw new Error("Invalid form version item."); // 該当しない場合はエラー
};

// レポート更新時に全てのフォームを検証する
// this.$validator.validateAll() では上手く検証できなかったため、formのDOMを取得して検証する
export const validateEditForms = async (
  vc, // VueComponent
  document // Document Object
) => {
  const formElements = document.querySelectorAll(
    "[name^='operation-report-form-enable']"
  ); // NOTE: operation-report-form-enable で始まるname属性を持つ要素(活性状態のフォーム)を取得
  const formNames = Array.from(formElements).map(element =>
    element.getAttribute("name")
  );

  const validatePromises = formNames.map(async formName => {
    const isValid = vc.$validator.validate(formName);
    return isValid;
  });
  try {
    const validateResult = await Promise.all(validatePromises);
    const reducedResult = validateResult.reduce(
      (acc, cur) => acc && cur,
      validatePromises.length > 0 // formがない場合はfalseを返す
    ); // 全ての検証結果がtrueの場合にtrueを返す
    return reducedResult;
  } catch {
    return false;
  }
};
